/*
  Pairs purchase order lines with invoice lines for the side-by-side diff. Scores per pair:
    3  product reference equals the article code on the invoice line
    2  descriptions share at least half of their words
    1  same quantity and a unit price within 1 %
  Greedy one-to-one assignment, highest scores first. Purely informational: the match never blocks anything.
*/
const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length >= 3);

function jaccard(a, b) {
  if (!a.length || !b.length) return 0;
  const setB = new Set(b);
  const common = a.filter((token) => setB.has(token)).length;
  return common / new Set([...a, ...b]).size;
}

function scorePair(po, inv) {
  const poRef = String(po.Productref || '')
    .trim()
    .toLowerCase();
  const invRef = String(inv.ArtikelCode || '')
    .trim()
    .toLowerCase();
  if (poRef && invRef && poRef === invRef) return 3;
  if (jaccard(normalize(`${po.tekstlijn} ${po.ProductNameNL || ''}`), normalize(inv.tekstlijn)) >= 0.5) return 2;
  const qtyEqual = Number(po.aantal) > 0 && Math.abs(Number(po.aantal) - Number(inv.aantal)) < 0.001;
  const poPrice = Number(po.AP);
  const invPrice = Number(inv.VP);
  if (qtyEqual && poPrice > 0 && invPrice > 0 && Math.abs(poPrice - invPrice) / poPrice <= 0.01) return 1;
  return 0;
}

export function matchLines(poLines = [], invoiceLines = []) {
  const candidates = [];
  poLines.forEach((po, poIndex) =>
    invoiceLines.forEach((inv, invIndex) => {
      const score = scorePair(po, inv);
      if (score > 0) candidates.push({ poIndex, invIndex, score });
    })
  );
  candidates.sort((a, b) => b.score - a.score || a.poIndex - b.poIndex);
  const usedPo = new Set();
  const usedInv = new Set();
  const pairs = [];
  for (const candidate of candidates) {
    if (usedPo.has(candidate.poIndex) || usedInv.has(candidate.invIndex)) continue;
    usedPo.add(candidate.poIndex);
    usedInv.add(candidate.invIndex);
    pairs.push({ po: poLines[candidate.poIndex], inv: invoiceLines[candidate.invIndex], score: candidate.score });
  }
  return {
    pairs,
    unmatchedPo: poLines.filter((_, index) => !usedPo.has(index)),
    unmatchedInvoice: invoiceLines.filter((_, index) => !usedInv.has(index))
  };
}

export function lineDelta(po, inv) {
  const qtyDelta = Number(inv.aantal) - Number(po.aantal);
  const priceDelta = Number(inv.VP) - Number(po.AP);
  const totalDelta = Number(inv.VP) * Number(inv.aantal) - Number(po.AP) * Number(po.aantal);
  return { qtyDelta, priceDelta, totalDelta };
}
